import { useMemo } from 'react'
import { Control, FieldArrayWithId, useWatch } from 'react-hook-form'

import { Alert } from 'components/FbUI/Alert'

import { TextInput } from 'components/FormUtils/TextInput'
import {
  findCampaignColumnVariablesWithMissingValues,
  findUnknownTemplateVariables,
  getMissingValuesLabel,
  mergeVariables,
} from '../email-variables'
import { useCampaignVariables } from '../columns/useCampaignVariables'
import { EmailBodyEditor } from './EmailBodyEditor'
import { useDebouncedFlag } from 'hooks/useDebouncedFlag'

export interface SequenceStepForm {
  id?: number // Bison step ID - present for existing steps, undefined for new
  email_subject: string
  email_body: string
  wait_in_days: number
  // Variant authoring (flat-with-adjacency model): a variant is a flat row tagged
  // is_variant=true kept adjacent right after its family original. wait_in_days is
  // shared at the slot (the original's value). variant_from_step_id is the saved
  // original's id when known; the order-ref link field is derived at save time.
  is_variant?: boolean
  variant_from_step_id?: number | null
  // Display-only mirror of the EmailBison `active` flag (toggled via its own
  // endpoint, NOT the sequence save payload). Defaults active.
  active?: boolean
  // Reply within the prior step's thread (true) vs start a new thread (false).
  // Defaults on for steps after the first; step 1 is always false (it starts the
  // thread). Left unset on `defaultStep` (the step-1 base case → false on emit).
  thread_reply?: boolean
}

export interface SequenceForm {
  title: string
  steps: SequenceStepForm[]
}

export const defaultStep: SequenceStepForm = {
  email_subject: '',
  email_body: '',
  wait_in_days: 1,
}

interface SequenceStepFormItemProps {
  field: FieldArrayWithId<SequenceForm, 'steps', 'id'>
  index: number
  control: Control<SequenceForm>
  canRemove: boolean
  isLast: boolean
  onRemove: () => void
  campaignId?: number
}

export function SequenceStepFormItem({
  field,
  index,
  control,
  canRemove,
  isLast,
  onRemove,
  campaignId,
}: SequenceStepFormItemProps) {
  const customVariables = useCampaignVariables(campaignId)
  const variables = useMemo(
    () => mergeVariables(customVariables),
    [customVariables]
  )
  const subject = useWatch({
    control,
    name: `steps.${index}.email_subject`,
  })
  const missingSubjectVariables = useMemo(
    () =>
      findCampaignColumnVariablesWithMissingValues(variables, [
        typeof subject === 'string' ? subject : '',
      ]),
    [subject, variables]
  )
  const unknownSubjectVariables = useMemo(
    () =>
      findUnknownTemplateVariables(variables, [
        typeof subject === 'string' ? subject : '',
      ]),
    [subject, variables]
  )

  // Mirror the EmailBodyEditor pattern: hide subject validation banners
  // until the user pauses typing so transient mid-token states like `{F}`
  // do not flash a warning the moment they begin typing a real token.
  const showMissingSubjectBanner = useDebouncedFlag(
    missingSubjectVariables.length > 0
  )
  const showUnknownSubjectBanner = useDebouncedFlag(
    unknownSubjectVariables.length > 0
  )

  return (
    <div
      key={field.id}
      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
            {index + 1}
          </span>
          <span className="text-sm font-medium text-gray-700">
            Step {index + 1}
          </span>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-sm text-red-600 hover:text-red-800"
          >
            Remove
          </button>
        )}
      </div>

      <div className="space-y-3">
        <TextInput
          name={`steps.${index}.email_subject`}
          control={control}
          label="Subject"
          placeholder="Email subject line"
          rules={{ required: 'Subject is required' }}
        />
        {showMissingSubjectBanner && missingSubjectVariables.length > 0 && (
          <Alert
            color="warning"
            description={
              <>
                Subject uses campaign column variables with missing values:{' '}
                {missingSubjectVariables
                  .map(
                    (variable) =>
                      `${variable.label} (${getMissingValuesLabel(variable)})`
                  )
                  .join(', ')}
              </>
            }
          />
        )}
        {showUnknownSubjectBanner && unknownSubjectVariables.length > 0 && (
          <Alert
            color="warning"
            description={
              <>
                Subject uses variables that are not available for this campaign:{' '}
                {unknownSubjectVariables.join(', ')}
              </>
            }
          />
        )}

        <EmailBodyEditor
          name={`steps.${index}.email_body`}
          control={control}
          label="Body"
          placeholder="Write your email content here..."
          campaignId={campaignId}
        />

        {!isLast && (
          <TextInput
            name={`steps.${index}.wait_in_days`}
            control={control}
            label="Wait before next step (days)"
            type="number"
            placeholder="Days to wait before next step"
            rules={{
              required: 'Wait time is required',
              min: { value: 0, message: 'Must be 0 or greater' },
            }}
          />
        )}
      </div>
    </div>
  )
}
